import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import { selectAgentMode } from '../../redux/features/agentMode/agentMode.selector';
import { armIntercept } from '../../redux/features/agentMode/agentMode.slice';
import { selectShowsTakeoverAlert } from '../../redux/features/ai/ai.selector';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import logger from '../../utils/logger';

/**
 * HITL-1650 — when the AI asks for a human, put the agent where they can be one.
 *
 * The status band blinks red and says "take over now". Until now that was all it
 * did: the agent still had to notice it, find the mode switch and choose
 * `Intercept` before the console would let them touch anything — in the seconds
 * where the customer is waiting and the AI has already given up. This closes
 * that gap, so the gates are open by the time they look.
 *
 * ## It ARMS Intercept; it does not take over
 *
 * `armIntercept` sets the mode and nothing else. No `agent_interception` goes
 * out, no TTS, no `sendOrder`, and `isAIAutoMode` is untouched — that flag is
 * the AI's own control state, and mutating it from the console to chase a
 * display condition is what caused the dropped-order incident behind MR !1534.
 * HITL-1604's distinction is the whole point: `Intercept` with nothing sent is
 * permission without consequence. The agent arrives with the gates open and
 * still chooses whether to walk through them.
 *
 * ## It does not silence the alert, and it does not trap the agent
 *
 * Both follow from the mode being marked `enteredBy: 'system'`:
 *
 *   - the band keeps blinking, because the blink stops when the AGENT answers
 *     the alert and nobody has. An auto-arm that switched the alert off would
 *     hide the AI's request from the one person it is addressed to.
 *   - the switch stays unlocked, so `Shadow` is still one press away. A one-way
 *     lock on a mode the agent never chose is the console overruling them.
 *
 * The request is answered by the agent ACTING, not by a mode flipping
 * underneath them.
 *
 * ## Why this emits the metric and the promote hook does not
 *
 * `agent_mode_change` with `trigger: 'system'` is the half of HITL-1605's
 * contract nothing had written yet — the field was made required, rather than
 * optional, specifically so "the agent took over at 14:02" and "the console took
 * over for them at 14:02" could never be confused. Paired with the agent's own
 * press that usually follows, the gap between the two is the only measurement of
 * whether arming early actually helps, which is the question this ticket exists
 * to answer. Emitting is safe by construction: the metric controls nothing at
 * either end.
 *
 * ## One attempt per session
 *
 * Latched on the session id, not on a boolean. The alert is sticky to session
 * end and the agent may step straight back to `Shadow`, so an effect keyed on
 * the alert alone would re-arm and drag them back — the console fighting the
 * agent over their own console. One attempt; after that the decision is theirs.
 */
const useAutoInterceptOnTakeover = () => {
  const dispatch = useAppDispatch();
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  const showsTakeoverAlert = useAppSelector(selectShowsTakeoverAlert);
  const mode = useAppSelector(selectAgentMode);
  const sessionId = useAppSelector(selectSessionId);

  const handledSessionRef = useRef<string | null>(null);

  useEffect(() => {
    // Flag off is production today: the blink asks, and the agent answers it by
    // hand exactly as they do now.
    if (!isShadowModeEnabled) return;
    // `sessionId` is not re-checked here: `selectShowsTakeoverAlert` already
    // requires one, and a second copy of that term would be dead code
    // pretending to be a guard. It is still read below, as the latch's key.
    if (!showsTakeoverAlert) return;
    if (handledSessionRef.current === sessionId) return;

    // Latched BEFORE the mode check, so a session where the agent was already in
    // `Intercept` when the AI escalated is spent too. Otherwise their stepping
    // back to `Shadow` later in that same session would let this fire on the
    // still-true alert and overrule them.
    handledSessionRef.current = sessionId;
    if (mode === AgentMode.intercept) return;

    dispatch(armIntercept());
    dispatch(
      messagingActions.sendAgentMetric({
        data: {
          metric: 'agent_mode_change',
          // Provably `Shadow`: the guard above returned on anything else.
          from: mode,
          to: AgentMode.intercept,
          trigger: 'system',
        },
      })
    );
    logger.info({
      message: 'AI asked for a takeover — armed Intercept for the agent',
      moreInfo: `session ${sessionId}`,
    });
    // `mode` is deliberately absent from the deps: this must run when the ALERT
    // arrives, not every time the agent moves the switch afterwards. It is read
    // as the latest render's value at that moment, which is what the guard above
    // needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isShadowModeEnabled, showsTakeoverAlert, sessionId]);
};

export default useAutoInterceptOnTakeover;
